import { useCallback, useMemo } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'

/**
 * A list's position, in its own URL.
 *
 * `listMemory` remembers where a list was left for the tab that is looking at it, and
 * that is enough for a click and a Back inside the running app. This is the other half,
 * and the one a reader can see: the scope, the page and the search live in the address
 * as `?scope=SUSPENDED&page=4`, so the entry the browser goes BACK to already carries
 * them. Nothing has to be replayed from storage, a refresh keeps the view, and the
 * address of "page 4 of the suspended users" can be sent to someone else.
 *
 * WRITTEN WITH `replace`, never a push: paging through a list is not four entries of
 * history to walk back out through, it is one list being read. The push happens when a
 * record is opened, which is the only move worth a Back.
 *
 * Defaults are left OUT of the URL — `/users` means the scope and page the screen opens
 * on — so the common case stays a clean address and only what was deliberately changed
 * shows up.
 *
 * @param {Record<string, string|number>} defaults  MODULE-LEVEL constant, one entry per
 *   value; its type decides how the parameter is read back.
 * @returns {[Record<string, string|number>, (patch: object) => void]}
 */
/**
 * The hash the address bar has RIGHT NOW.
 *
 * Both setters below carry the hash through their writes, or a tabbed screen would lose its
 * section every time a page or a filter moved. They used to carry the one `useLocation()`
 * gave them, which is the hash of the render that made the setter — and that is one render
 * behind whenever a screen changes its tab and its page in the same gesture. On the
 * assessment report, choosing a tab wrote `#failed` and the page-reset that follows it
 * immediately overwrote the address with the hash from before the click: the tab strip moved,
 * the address never did, and a reload came back on the first tab.
 *
 * `navigate` writes through `history.replaceState` synchronously, so the DOM's own hash is
 * always the freshest answer available at the moment a setter runs.
 */
const liveHash = () => window.location.hash

export default function useListParams(defaults) {
  const [params] = useSearchParams()
  const navigate = useNavigate()
  /* THE HASH HAS TO BE CARRIED. `setSearchParams` navigates to `"?" + params` and
     react-router parses that as a path with NO hash, so every write would erase the
     `#section` a tabbed screen keeps its position in — a filter would silently close the
     tab it was filtering. */

  const values = useMemo(() => {
    const next = {}
    for (const [key, fallback] of Object.entries(defaults)) {
      const raw = params.get(key)
      if (raw == null || raw === '') {
        next[key] = fallback
      } else if (typeof fallback === 'number') {
        const parsed = Number(raw)
        next[key] = Number.isFinite(parsed) && parsed > 0 ? parsed : fallback
      } else if (typeof fallback === 'boolean') {
        next[key] = raw === 'true'
      } else {
        next[key] = raw
      }
    }
    return next
  }, [params, defaults])

  const update = useCallback(
    (patch) => {
      const next = new URLSearchParams(params)
      for (const [key, value] of Object.entries(patch)) {
        if (value == null || value === '' || value === defaults[key]) next.delete(key)
        else next.set(key, String(value))
      }
      const search = next.toString()
      navigate({ search: search ? `?${search}` : '', hash: liveHash() }, { replace: true })
    },
    [navigate, params, defaults],
  )

  return [values, update]
}

/**
 * One page number, in the address — the server-paged screens' half of the same contract.
 *
 * A table that pages itself gets this from DataTable's `pageParam`; a screen that pages
 * through the SERVER owns the number and asks for it here.
 */
export function usePageParam(name = 'page') {
  const [params] = useSearchParams()
  const navigate = useNavigate()
  const raw = Number(params.get(name))
  const page = Number.isFinite(raw) && raw > 0 ? raw : 1

  const setPage = useCallback(
    (next) => {
      const value = typeof next === 'function' ? next(page) : next
      const search = new URLSearchParams(params)
      if (value > 1) search.set(name, String(value))
      else search.delete(name)
      const query = search.toString()
      // The hash goes with it — see the note in `useListParams`.
      navigate({ search: query ? `?${query}` : '', hash: liveHash() }, { replace: true })
    },
    [name, page, params, navigate],
  )

  return [page, setPage]
}
